// app/javascript/controllers/global_drawer_controller.js
// Global drawer controller for dynamic content loading via Turbo Streams
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "panel", "content"]

  connect() {
    this.isOpen = false
      this.isLoading = false
      this.pendingOpenTimeout = null
      this.lastSrc = null
    
    // Listen for custom events to open/close the drawer
    this.boundHandleOpen = this.handleOpenEvent.bind(this)
    this.boundHandleClose = this.handleCloseEvent.bind(this)
    
    document.addEventListener("global-drawer:open", this.boundHandleOpen)
    document.addEventListener("global-drawer:close", this.boundHandleClose)
    
    // Listen for turbo:frame-load to auto-open when content is loaded
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this)
    this.element.addEventListener("turbo:frame-load", this.boundHandleFrameLoad)

    // Detect turbo-stream responses (e.g. limit_reached modal) that bypass the frame
    this.boundHandleFrameResponse = this.handleFrameResponse.bind(this)
    this.element.addEventListener("turbo:before-fetch-response", this.boundHandleFrameResponse)
    
    // Ensure drawer is hidden initially
    this.element.classList.add("hidden")
  }

  disconnect() {
    document.removeEventListener("global-drawer:open", this.boundHandleOpen)
    document.removeEventListener("global-drawer:close", this.boundHandleClose)
    this.element.removeEventListener("turbo:frame-load", this.boundHandleFrameLoad)
    this.element.removeEventListener("turbo:before-fetch-response", this.boundHandleFrameResponse)

      // Clear any pending timeout
      if (this.pendingOpenTimeout) {
          clearTimeout(this.pendingOpenTimeout)
          this.pendingOpenTimeout = null
      }
    
    if (this.isOpen) {
      document.body.style.overflow = ""
    }
  }

  handleOpenEvent(event) {
    const { src, height } = event.detail || {}

      // Debounce: Ignore duplicate requests for the same src within 100ms
      if (src === this.lastSrc && this.pendingOpenTimeout) {
          return
      }

      // Clear any pending timeout
      if (this.pendingOpenTimeout) {
          clearTimeout(this.pendingOpenTimeout)
          this.pendingOpenTimeout = null
      }

      this.lastSrc = src

      // Set a short timeout to debounce multiple rapid clicks
      this.pendingOpenTimeout = setTimeout(() => {
          this.pendingOpenTimeout = null
          this._handleOpen(src, height)
      }, 50)
  }

    _handleOpen(src, height) {
        // Prevent duplicate opens while loading
        if (this.isLoading) return

        // If already open with same src, ignore
        if (src && this.hasContentTarget && this.isOpen && this.contentTarget.src === src) return


        // Close current drawer before opening a new one to avoid stacking
        if (this.isOpen) {
            this.close()
            // Wait for close animation before opening new content
            setTimeout(() => this._loadAndOpen(src, height), 350)
        } else {
            this._loadAndOpen(src, height)
        }
  }

    _loadAndOpen(src, height) {
    // Update panel height if provided
    if (height && this.hasPanelTarget) {
      this.panelTarget.style.maxHeight = height
    }
    
    // Load content via Turbo Frame if src provided
    if (src && this.hasContentTarget) {
        this.isLoading = true
      this.contentTarget.src = src
      // The frame will trigger turbo:frame-load when loaded, which will open the drawer
    } else {
      // If no src, just open (content already in place)
      this.open()
    }
  }

  handleCloseEvent() {
    this.close()
  }

  handleFrameResponse(event) {
    const contentType = event.detail?.fetchResponse?.response?.headers?.get("content-type") || ""
    if (contentType.includes("text/vnd.turbo-stream.html") && this.isLoading) {
      this.isLoading = false
      this.lastSrc = null
    }
  }

  handleFrameLoad(event) {
    // Auto-open drawer when frame content is loaded
      // Only handle if this is our specific content frame and we're expecting content
      if (event.target === this.contentTarget && this.isLoading) {
          this.isLoading = false
      this.open()
    }
  }

  open() {
    if (this.isOpen) return

    // Remove hidden class to show the drawer container
    this.element.classList.remove("hidden")
    
    // Use requestAnimationFrame to ensure the hidden class removal is processed
    requestAnimationFrame(() => {
      // Show overlay
      if (this.hasOverlayTarget) {
        this.overlayTarget.classList.remove("opacity-0", "pointer-events-none")
        this.overlayTarget.classList.add("opacity-100", "pointer-events-auto")
      }

      // Slide up panel
      if (this.hasPanelTarget) {
        this.panelTarget.classList.remove("translate-y-full")
        this.panelTarget.classList.add("translate-y-0")
      }

      // Enable pointer events on container
      this.element.classList.remove("pointer-events-none")
      this.element.classList.add("pointer-events-auto")
    })

    // Prevent body scroll
    document.body.style.overflow = "hidden"

    this.isOpen = true
  }

  close() {
    if (!this.isOpen) return

    // Hide overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("opacity-0", "pointer-events-none")
      this.overlayTarget.classList.remove("opacity-100", "pointer-events-auto")
    }

    // Slide down panel
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("translate-y-full")
      this.panelTarget.classList.remove("translate-y-0")
    }

    // Disable pointer events on container
    this.element.classList.add("pointer-events-none")
    this.element.classList.remove("pointer-events-auto")

    // Restore body scroll
    document.body.style.overflow = ""

    // Hide the drawer and reset content after animation completes
    setTimeout(() => {
      if (!this.isOpen) {
        this.element.classList.add("hidden")
        this.resetContent()
      }
    }, 300) // Match the transition duration

    this.isOpen = false
      this.isLoading = false
  }

  // Reset the drawer content to initial spinner state
  resetContent() {
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = `
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      `
      this.contentTarget.removeAttribute("src")
    }
  }

  // Static method to open the global drawer from anywhere
  static open(src, options = {}) {
    document.dispatchEvent(new CustomEvent("global-drawer:open", {
      bubbles: true,
      detail: { src, ...options }
    }))
  }

  // Static method to close the global drawer from anywhere
  static close() {
    document.dispatchEvent(new CustomEvent("global-drawer:close", {
      bubbles: true
    }))
  }
}
